/**
 * Page templates — drag a page tile from the palette onto the canvas to
 * add a page. A template names the section templates to pre-seed; the new
 * page node carries them in `data.seedSections` and PageNode seeds them on
 * first mount (when its grid is still empty).
 */
import type { PageNodeData } from "./PageNode.js";
import { SEED_WIDTH, SEED_HEIGHT } from "./initialSitemap.js";

export interface PageTemplate {
  readonly id: string;
  readonly label: string;
  readonly icon: string;
  /** Section-template ids to pre-seed. Empty = a blank page. */
  readonly sections: ReadonlyArray<string>;
}

export const PAGE_TEMPLATES: PageTemplate[] = [
  { id: "blank", label: "Blank page", icon: "📄", sections: [] },
  {
    id: "landing",
    label: "Landing",
    icon: "🚀",
    sections: ["hero", "three-features", "contact-form"],
  },
  { id: "contact", label: "Contact", icon: "✉️", sections: ["contact-form"] },
];

export interface NewPageNode {
  id: string;
  type: "page";
  position: { x: number; y: number };
  // Seed dimensions so xyflow doesn't render the freshly-added node
  // `visibility:hidden` until its first measurement (which leaves it
  // invisible + non-interactive on the frame it appears). The ResizeObserver
  // overwrites `measured` with the true size immediately. See initialSitemap.
  initialWidth: number;
  initialHeight: number;
  data: PageNodeData;
}

let _pageCounter = 0;
function pageId(): string {
  // Unique per session; Date.now + counter avoids collisions on rapid drops.
  return `page-${Date.now().toString(36)}-${++_pageCounter}`;
}

/**
 * Build a new page node from a template at a flow position.
 *
 * `label` defaults to the template label, except `blank` → `Page N`
 * (N from the existing node count). `data.seedSections` is ALWAYS set
 * (including `[]` for blank) — the empty-vs-absent distinction is what
 * makes a blank page seed nothing while the un-templated Home page seeds
 * the default section.
 */
export function createPageNodeFromTemplate(
  template: PageTemplate,
  position: { x: number; y: number },
  existingNodes: ReadonlyArray<{ id: string }>,
): NewPageNode {
  const label =
    template.id === "blank"
      ? `Page ${existingNodes.length + 1}`
      : template.label;
  return {
    id: pageId(),
    type: "page",
    position,
    initialWidth: SEED_WIDTH,
    initialHeight: SEED_HEIGHT,
    data: { label, seedSections: [...template.sections] },
  };
}
